import React, { useRef, useState, useEffect, useCallback } from 'react'
import TextInputElement from 'components/forms/TextInputElement'
import Button from 'components/common/Button'
import DateModal from './DateModal/DateModal'
import HobnobSelect from 'components/common/HobnobSelect'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import LabelIcon from 'assets/icons/label-icon.png'
import LocationIcon from 'assets/icons/location-icon.png'
import DateIcon from 'assets/icons/date-icon.png'
import HobnobSpaceIcon from 'assets/icons/hobnob-space-icon.png'
import RightIconWhite from 'assets/icons/right-icon-white.png'
import CheckMarkIcon from 'assets/icons/check-mark-icon.png'
import { changeEventForm } from 'src/actions/event'
import moment from 'moment'
import cn from 'classnames'
import { renderTimezoneAbbreviation } from 'services/date_helpers'
import { capitalized } from 'services/utils'
import styles from './first-step.scss'
import LocationAutoCompleteInput from 'components/map/LocationAutoCompleteInput'
import {
  createApplePlace,
  mapLocationDataBetweenApplePlaceAndGooglePlace
} from '../../../../actions/places'
import HobnobModal from 'components/common/HobnobModal'
import { Select } from 'antd'
import { containsURL } from 'src/services/url_validation'
import Tour from 'src/components/common/Tour/Tour'
import { TOUR_STEPS, getTourState, setTourFlag, setTourSkip } from 'src/services/tour_storage_utils'

const FirstStep = ({
  onButtonClick
}) => {
  const dispatch = useDispatch()
  const eventForm = useSelector(state => state.frontend.eventForm)
  const userId = useSelector(state => state.userId)
  const communityMemberList = useSelector(state => state.frontend.communityMemberList)

  const [isOpenDateModal, setIsOpenDateModal] = useState(false)
  const [nameInputSelectionStart, setNameInputSelectionStart] = useState(0)
  const [nameInputSelectionEnd, setNameInputSelectionEnd] = useState(0)
  const [isLocationSearchModalShown, setIsLocationSearchModalShown] = useState(false)
  const [isLocationSearchPanelShown, setIsLocationSearchPanelShown] = useState(false)
  const [selectedAppleLocation, setSelectedAppleLocation] = useState(undefined)
  const nameInputRef = useRef(null)
  const basicInfoRef = useRef(null)
  const [showTour, setShowTour] = useState(false)
  const stepConfig = TOUR_STEPS.find(step => step.key === 'basicInfo')
  const tourState = getTourState()
  const showBasicInfoTour = !tourState.isBasicInfoTourOpened
  const isSkipped = tourState.isSkipped

  useEffect(() => {
    if (nameInputRef.current && eventForm.name) {
      nameInputRef.current.setSelectionRange(nameInputSelectionStart, nameInputSelectionEnd)
    }
  }, [eventForm.name])

  const handleBasicInfoRef = useCallback((domElement) => {
    basicInfoRef.current = domElement
    if (domElement && !showTour) {
      setShowTour(true)
    }
  }, [showTour])

  const handleChange = (type, value) => {
    switch (type) {
      case 'name':
        if (!nameInputRef.current) {
          nameInputRef.current = value.target
        }
        const nameStr = capitalized(nameInputRef.current.value)
        dispatch(changeEventForm({ name: nameStr }))
        setNameInputSelectionStart(nameInputRef.current.selectionStart)
        setNameInputSelectionEnd(nameInputRef.current.selectionEnd)
        break
      case 'community':
        dispatch(changeEventForm({ external_community_id: value || null }))
        break
      default:
        dispatch(changeEventForm({ [type]: value }))
    }
  }

  const getCommunitiesList = () => {
    const ValidList = ['admin', 'member', 'moderator']
    const lists = communityMemberList.filter(item => {
      if (ValidList.indexOf(item.my_community_member?.role) > -1) {
        return { value: item.id, text: item.name }
      }
      return false
    })
    const communitiesList = lists.map(item => ({ value: item.id, text: item.name }))
    communitiesList.unshift({ value: '', text: 'None' })
    return communitiesList
  }

  const handleNextStep = () => {
    window.mixpanel.track('SELECT DESIGN Clicked', {
      user_id: userId
    })
    if (!selectedAppleLocation) {
      onButtonClick()
      return
    }
    const place = selectedAppleLocation
    dispatch(createApplePlace({
      placeData: mapLocationDataBetweenApplePlaceAndGooglePlace(place, 'google'),
      onSuccessCallback: response => {
        if (response.data?.apple_place_create?.successful) {
          const createdPlace = response.data.apple_place_create.result
          handleChange('apple_place_id', createdPlace.id)
          if (response.data.apple_place_create.messages?.length) {
            console.log('create place successful with messages')
            console.log(response.data.apple_place_create.messages)
          }
          onButtonClick()
        } else if (response.errors) {
          console.warn('failed to create apple place:')
          console.warn(response.errors)
        } else {
          console.error(response)
        }
      }
    }))
  }

  const onLocationSelected = location => {
    if (location.latitude) {
      setIsLocationSearchModalShown(false)
      setSelectedAppleLocation(location)
      handleChange('venue', location)
      handleChange('custom_venue_name', null)
    } else {
      setIsLocationSearchModalShown(false)
      setSelectedAppleLocation(undefined)
      handleChange('apple_place_id', null)
      handleChange('venue', null)
      handleChange('custom_venue_name', location.name ? location.name : null)
    }
  }

  const handleCloseTour = () => {
    setTourFlag('isBasicInfoTourOpened', true)
    setShowTour(false)
  }

  const handleSkipTutorials = () => {
    setTourSkip(true)
    setShowTour(false)
  }

  let displayLocation = ''
  if (eventForm.venue) {
    displayLocation = eventForm.venue.name ? eventForm.venue.name : eventForm.venue.street
  } else {
    displayLocation = eventForm.custom_venue_name
  }

  const eventNameErrorText = eventForm.name && containsURL(eventForm.name)
    ? 'No URLs allowed'
    : ''

  return (
    <div className={styles['first-step-container']}>
      <div ref={handleBasicInfoRef}>
        <div className={styles['input-container']}>
          <section className={styles['input-wrapper']}>
            <img src={LabelIcon} alt="" width="18" />
            <TextInputElement
              className={styles['input-inner']}
              placeholder="Your Event Name"
              onChange={event => handleChange('name', event)}
              valid
              value={eventForm.name}
              maxLength={50}
            />
            <img
              className={cn({ [styles['icon-hidden']]: !eventForm.name })}
              src={CheckMarkIcon}
              width="18"
            />
          </section>
          <div
            className={cn(styles['error-text'], {
              [styles['error-text-hidden']]: !eventNameErrorText
            })}
          >
            {eventNameErrorText}
          </div>
        </div>

        <section className={styles['optional-text']}>optional</section>

        <section
          className={styles['input-wrapper']}
          onClick={() => setIsOpenDateModal(true)}
        >
          <img src={DateIcon} alt="" width="18" />
          <section className={styles['input-box']}>
            <TextInputElement
              className={styles['input-inner']}
              placeholder="Date & Time"
              onChange={() => { }}
              valid
              value={
                eventForm.starts_at
                  ? `${moment(eventForm.starts_at).format(
                    'ddd, MMM DD, YYYY, h:mm A'
                  )} - ${renderTimezoneAbbreviation(eventForm.timezone)}`
                  : ''
              }
              readOnly
            />
            {eventForm.ends_at && (
              <TextInputElement
                className={styles['input-inner']}
                placeholder="Date & Time"
                onChange={() => { }}
                valid
                value={`${moment(eventForm.ends_at).format(
                  'ddd, MMM DD, YYYY, h:mm A'
                )} - ${renderTimezoneAbbreviation(eventForm.timezone)}`}
                readOnly
              />
            )}
          </section>
        </section>
        {/* trigger for desktop view */}
        <section
          className={cn(styles['input-wrapper'], styles['desktop'])}
          onClick={() => setIsLocationSearchModalShown(true)}
        >
          <img src={LocationIcon} alt="" width="18" />
          <div className={styles['input-box']}>
            <TextInputElement
              className={styles['input-inner']}
              placeholder="Location"
              valid
              readOnly
              value={displayLocation}
            />
          </div>
        </section>
        {/* trigger for mobile web view */}
        <section
          className={cn(styles['input-wrapper'], styles['mobile-web'])}
          onClick={() => setIsLocationSearchPanelShown(true)}
        >
          <img src={LocationIcon} alt="" width="18" />
          <div className={styles['input-box']}>
            <TextInputElement
              className={styles['input-inner']}
              placeholder="Location"
              valid
              readOnly
              value={displayLocation}
            />
          </div>
        </section>
        {
          userId && (
            <>
              <section className={cn(styles['input-wrapper'], styles['mobile-web'])}>
                <img src={HobnobSpaceIcon} alt="" width="18" />
                <HobnobSelect
                  value={eventForm.external_community_id}
                  options={getCommunitiesList()}
                  onChange={value => handleChange('community', value)}
                  inputClassName={styles['input-inner']}
                  textBlank
                  placeholder="Hobnob Space"
                />
              </section>

              <section className={cn(styles['input-wrapper'], 'web-view')}>
                <img src={HobnobSpaceIcon} alt="" width="18" />
                <Select
                  bordered={false}
                  size="small"
                  className={styles['input-inner']}
                  fieldNames={{ label: 'text' }}
                  defaultValue={
                    eventForm.external_community_id === null ? '' : eventForm.external_community_id
                  }
                  options={getCommunitiesList()}
                  onChange={value => handleChange('community', value)}
                />
              </section>
            </>
          )
        }
      </div>

      <section className={styles['button-wrapper']}>
        <Button
          disabled={!eventForm.name || eventNameErrorText !== ''}
          className={styles['button-inner']}
          onClick={handleNextStep}
        >
          <span>select design</span>
          <img src={RightIconWhite} alt="" width="24" />
        </Button>
      </section>

      <DateModal
        visible={isOpenDateModal}
        onClose={() => setIsOpenDateModal(false)}
      />

      <HobnobModal
        show={isLocationSearchModalShown}
        onClose={() => setIsLocationSearchModalShown(false)}
      >
        <LocationAutoCompleteInput
          onLocationSelected={result => {
            onLocationSelected(result)
          }}
        />
      </HobnobModal>

      {
        isLocationSearchPanelShown && (
          <section className={cn(styles['location-search-panel'])}>
            <LocationAutoCompleteInput
              onLocationSelected={result => {
                setIsLocationSearchPanelShown(false)
                onLocationSelected(result)
              }}
              onCancelClicked={() => {
                setIsLocationSearchPanelShown(false)
              }}
            />
          </section>
        )
      }

      <Tour
        open={showTour && showBasicInfoTour && !isSkipped}
        steps={[{ ...stepConfig, target: basicInfoRef }]}
        mask
        arrow
        onSkipTutorials={handleSkipTutorials}
        onFinish={handleCloseTour}
        onClose={handleCloseTour}
      />

    </div >
  )
}

FirstStep.propTypes = {
  onButtonClick: PropTypes.func
}

export default FirstStep
